import {

  type Box,
  type Pt,
} from '../model/geom';
import {
  transformBlockChild,
  type BlockDef,
  type CadEntity,
} from '../model/entity';
import type { Layer, UnderlayRef } from '../model/document';
import type { Scene } from '../scene';
import type { Viewport } from './viewport';
import { SNAP_LABEL, type SnapResult } from '../snap/snap';
import { dimensionLabel } from '../commands/dimension';
import type { DimensionEntity } from '../model/entity';
import type { DisplayUnit } from '../model/units';
import { getUnderlayImage } from './underlayCache';

/**
 * 렌더러 (작업지시서 P2-02 / P2-03)
 *
 * 캔버스를 두 겹으로 나눈다:
 *   main    — 도면 엔티티. Scene 이나 뷰포트가 바뀔 때만 다시 그린다.
 *   overlay — 커서 십자선, 스냅 마커, 고무줄 미리보기, 선택 상자, 그립.
 *             매 프레임 지우고 다시 그린다.
 *
 * 이 분리가 없으면 마우스를 움직일 때마다 5만 개를 다시 그리게 되어
 * 팬/줌이 즉시 무너진다.
 *
 * 뷰포트 컬링은 SpatialGrid 가 담당한다 — 화면 밖 엔티티는 아예 순회하지 않는다.
 */

export interface RenderTheme {
  background: string;
  grid: string;
  gridMajor: string;
  cursor: string;
  selection: string;
  selectionFill: string;
  snapMarker: string;
  preview: string;
  windowBox: string;
  crossingBox: string;
  text: string;

  /**
   * 모든 엔티티를 이 색으로 강제한다 (인쇄용 흑백 플롯).
   *
   * ★ 표준 레이어 색(#E8E8E8 벽체, #D9D9D9 치수 …)은 **어두운 화면 배경**을
   *   전제로 고른 값이다. 흰 종이에 그대로 찍으면 거의 보이지 않는다.
   *   실제 CAD 도 플롯 스타일(CTB)로 화면색을 검은 펜에 매핑해서 출력한다.
   */
  entityColor?: string;

  /**
   * lineWeight(1/100 mm)를 화면 배율이 아니라 **종이 기준 절대 두께**로 환산한다.
   * 값은 픽셀/mm (인쇄면 dpi/25.4).
   *
   * ★ 화면에서는 선 두께가 줌에 따라 같이 커지는 게 자연스럽지만, 인쇄는 반대다.
   *   축척 1:100 에서 0.5mm 벽체를 화면 규칙대로 환산하면 0.06px 이 되어
   *   최소값 1px(300dpi 에서 0.085mm)로 깎인다 — 의도한 두께의 1/6 이라
   *   도면 전체가 실낱같이 흐려진다.
   */
  lineWeightPxPerMm?: number;
}

export const DARK_THEME: RenderTheme = {
  background: '#1b1b26',
  grid: '#26263a',
  gridMajor: '#32324a',
  cursor: '#d6e8ee',
  selection: '#5fd3f3',
  selectionFill: 'rgba(95, 211, 243, 0.12)',
  snapMarker: '#ffd24a',
  preview: '#9be8ff',
  windowBox: '#5fd3f3',
  crossingBox: '#7ee08a',
  text: '#d6d6e0',
};

/** 엔티티가 실제로 쓸 색 (ByLayer 처리). 인쇄 테마는 한 색으로 강제한다. */
function colorOf(e: CadEntity, layer: Layer | undefined, theme: RenderTheme): string {
  return theme.entityColor ?? e.color ?? layer?.color ?? '#cccccc';
}

/** 선 두께(1/100mm) → 픽셀. 최소 1px 보장. */
function widthOf(
  e: CadEntity,
  layer: Layer | undefined,
  vp: Viewport,
  theme: RenderTheme
): number {
  const lw = e.lineWeight ?? layer?.lineWeight ?? 25;

  // 인쇄: 종이 기준 절대 두께. 축척·줌과 무관해야 한다.
  if (theme.lineWeightPxPerMm) {
    return Math.max(1, (lw / 100) * theme.lineWeightPxPerMm);
  }

  // 화면: lineWeight 는 1/100 mm 이므로 µm 로는 ×10
  const px = vp.worldToPx(lw * 10);
  return Math.max(1, Math.min(8, px));
}

/* -------------------------------------------------------------------------- *
 * 메인 레이어 — 도면
 * -------------------------------------------------------------------------- */

export function renderScene(
  ctx: CanvasRenderingContext2D,
  scene: Scene,
  vp: Viewport,
  selection: Set<string>,
  theme: RenderTheme = DARK_THEME
): number {
  const { width, height } = vp;

  ctx.save();
  ctx.fillStyle = theme.background;
  ctx.fillRect(0, 0, width, height);

  drawGrid(ctx, vp, theme);
  drawUnderlays(ctx, scene.doc.underlays, vp);

  // 뷰포트 컬링 — 화면에 걸치는 것만 가져온다
  const visible = vp.visibleBox(50);
  const candidates = scene.queryBox(visible);

  // 레이어 순서대로 그린다 (아래 레이어가 먼저)
  const layerOrder = new Map<string, number>();
  for (const l of scene.doc.layers) layerOrder.set(l.code, l.sortOrder);
  candidates.sort(
    (a, b) => (layerOrder.get(a.layer) ?? 999) - (layerOrder.get(b.layer) ?? 999)
  );

  let drawn = 0;
  for (const e of candidates) {
    const layer = scene.layer(e.layer);
    if (layer && !layer.visible) continue;

    const selected = selection.has(e.id);
    ctx.strokeStyle = selected ? theme.selection : colorOf(e, layer, theme);
    ctx.fillStyle = ctx.strokeStyle;
    const w = widthOf(e, layer, vp, theme);
    ctx.lineWidth = selected ? w + 1 : w;
    ctx.setLineDash(layer?.lineType === 'dashed' ? [6, 4] : []);

    drawEntity(ctx, e, vp, scene.blocks, theme, scene.doc.displayUnit);
    drawn++;
  }

  ctx.restore();
  return drawn;
}

function drawGrid(ctx: CanvasRenderingContext2D, vp: Viewport, theme: RenderTheme): void {
  const step = vp.gridStep(10);
  const stepPx = vp.worldToPx(step);
  if (stepPx < 4) return; // 너무 촘촘하면 그리지 않는다

  const box = vp.visibleBox();
  const x0 = Math.floor(box.minX / step) * step;
  const y0 = Math.floor(box.minY / step) * step;

  ctx.lineWidth = 1;
  ctx.setLineDash([]);

  for (let x = x0; x <= box.maxX; x += step) {
    // 10칸마다 진한 선 — 축척 감을 준다
    const major = Math.round(x / step) % 10 === 0;
    ctx.strokeStyle = major ? theme.gridMajor : theme.grid;
    const sx = Math.round(vp.toScreenX(x)) + 0.5;
    ctx.beginPath();
    ctx.moveTo(sx, 0);
    ctx.lineTo(sx, vp.height);
    ctx.stroke();
  }

  for (let y = y0; y <= box.maxY; y += step) {
    const major = Math.round(y / step) % 10 === 0;
    ctx.strokeStyle = major ? theme.gridMajor : theme.grid;
    const sy = Math.round(vp.toScreenY(y)) + 0.5;
    ctx.beginPath();
    ctx.moveTo(0, sy);
    ctx.lineTo(vp.width, sy);
    ctx.stroke();
  }
}

/**
 * 부착(Attach) 참조 이미지 — 엔티티보다 먼저(아래에) 그린다.
 * 선택·편집 대상이 아니므로 클릭 판정이나 그립은 없다(document.ts UnderlayRef 주석 참고).
 */
function drawUnderlays(ctx: CanvasRenderingContext2D, underlays: UnderlayRef[], vp: Viewport): void {
  for (const u of underlays) {
    if (!u.visible) continue;
    const img = getUnderlayImage(u.src);
    if (!img) continue; // 아직 로드 중 — 끝나면 underlayCache 가 다시 그리게 한다

    const topLeft = vp.toScreen(u.at);
    const wPx = vp.worldToPx(u.widthUm);
    const hPx = vp.worldToPx(u.heightUm);
    if (wPx <= 0 || hPx <= 0) continue;

    ctx.save();
    ctx.globalAlpha = u.opacity;
    ctx.drawImage(img, topLeft.x, topLeft.y, wPx, hPx);
    ctx.restore();
  }
}

/**
 * 엔티티 하나를 그린다. 색·두께·파선은 **호출자가 미리 ctx 에 설정한다.**
 *
 * 비교 화면(P4-09)은 레이어 색이 아니라 변경 종류(철거/신설/이설)로 칠하므로
 * 이 함수를 직접 쓴다. 그래서 export 한다.
 */
export function drawEntity(
  ctx: CanvasRenderingContext2D,
  e: CadEntity,
  vp: Viewport,
  blocks: Map<string, BlockDef>,
  theme: RenderTheme,
  unit: DisplayUnit = 'mm'
): void {
  switch (e.type) {
    case 'line': {
      ctx.beginPath();
      ctx.moveTo(vp.toScreenX(e.a.x), vp.toScreenY(e.a.y));
      ctx.lineTo(vp.toScreenX(e.b.x), vp.toScreenY(e.b.y));
      ctx.stroke();
      break;
    }

    case 'polyline': {
      if (e.points.length < 2) break;
      ctx.beginPath();
      const p0 = e.points[0]!;
      ctx.moveTo(vp.toScreenX(p0.x), vp.toScreenY(p0.y));
      for (let i = 1; i < e.points.length; i++) {
        const p = e.points[i]!;
        ctx.lineTo(vp.toScreenX(p.x), vp.toScreenY(p.y));
      }
      if (e.closed) ctx.closePath();
      ctx.stroke();
      break;
    }

    case 'circle': {
      const r = vp.worldToPx(e.radius);
      if (r < 0.5) break;
      ctx.beginPath();
      ctx.arc(vp.toScreenX(e.center.x), vp.toScreenY(e.center.y), r, 0, Math.PI * 2);
      ctx.stroke();
      break;
    }

    case 'arc': {
      const r = vp.worldToPx(e.radius);
      if (r < 0.5) break;
      ctx.beginPath();
      // 화면 Y축이 반전이므로 각도 부호를 뒤집고 반시계 플래그를 켠다
      ctx.arc(
        vp.toScreenX(e.center.x),
        vp.toScreenY(e.center.y),
        r,
        -e.startAngle,
        -e.endAngle,
        true
      );
      ctx.stroke();
      break;
    }

    case 'text': {
      const h = vp.worldToPx(e.height);
      if (h < 4) break; // 너무 작으면 생략 (성능)
      ctx.save();
      ctx.translate(vp.toScreenX(e.at.x), vp.toScreenY(e.at.y));
      ctx.rotate(-e.rotation);
      ctx.font = `${h}px 'Noto Sans KR', sans-serif`;
      ctx.textAlign = e.align === 'center' ? 'center' : e.align === 'right' ? 'right' : 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(e.text, 0, 0);
      ctx.restore();
      break;
    }

    case 'blockref': {
      const def = blocks.get(e.blockCode);
      if (!def) {
        // 정의를 못 찾으면 자리표시자를 그린다 — 조용히 사라지는 것보다 낫다
        const s = vp.toScreen(e.at);
        ctx.save();
        ctx.setLineDash([4, 3]);
        ctx.strokeRect(s.x - 12, s.y - 12, 24, 24);
        ctx.restore();
        break;
      }
      for (const child of def.entities) {
        drawEntity(ctx, transformBlockChild(child, e), vp, blocks, theme, unit);
      }
      break;
    }

    case 'dimension':
      drawDimension(ctx, e, vp, unit);
      break;
  }
}

/* -------------------------------------------------------------------------- *
 * 오버레이 — 커서 · 스냅 · 미리보기 · 선택 상자
 * -------------------------------------------------------------------------- */

export interface OverlayState {
  cursor: Pt | null;
  snap: SnapResult | null;
  preview: CadEntity[];
  /** 상자 선택 중일 때 */
  dragBox: { box: Box; crossing: boolean } | null;
  /** 선택된 엔티티의 그립 표시 */
  grips: Pt[];
  /** 치수 미리보기 표기에 쓰는 표시 단위 */
  unit?: DisplayUnit;
}

export function renderOverlay(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  state: OverlayState,
  blocks: Map<string, BlockDef>,
  theme: RenderTheme = DARK_THEME
): void {
  ctx.clearRect(0, 0, vp.width, vp.height);
  ctx.save();

  // 1. 고무줄 미리보기
  if (state.preview.length > 0) {
    ctx.strokeStyle = theme.preview;
    ctx.fillStyle = theme.preview;
    ctx.lineWidth = 1.2;
    ctx.setLineDash([5, 3]);
    for (const e of state.preview) {
      drawEntity(ctx, e, vp, blocks, theme, state.unit ?? 'mm');
    }
    ctx.setLineDash([]);
  }

  // 2. 상자 선택
  if (state.dragBox) {
    const { box, crossing } = state.dragBox;
    const x = vp.toScreenX(box.minX);
    const y = vp.toScreenY(box.maxY);
    const w = vp.worldToPx(box.maxX - box.minX);
    const h = vp.worldToPx(box.maxY - box.minY);

    ctx.strokeStyle = crossing ? theme.crossingBox : theme.windowBox;
    ctx.fillStyle = crossing ? 'rgba(126,224,138,0.10)' : 'rgba(95,211,243,0.10)';
    ctx.lineWidth = 1;
    // Crossing 은 파선, Window 는 실선 — AutoCAD 와 같다
    ctx.setLineDash(crossing ? [5, 3] : []);
    ctx.fillRect(x, y, w, h);
    ctx.strokeRect(x, y, w, h);
    ctx.setLineDash([]);
  }

  // 3. 그립 (선택된 객체의 파란 사각점)
  if (state.grips.length > 0) {
    ctx.fillStyle = theme.selection;
    for (const g of state.grips) {
      const s = vp.toScreen(g);
      ctx.fillRect(s.x - 3, s.y - 3, 6, 6);
    }
  }

  // 4. 커서 십자선
  if (state.cursor) {
    const s = vp.toScreen(state.cursor);
    ctx.strokeStyle = theme.cursor;
    ctx.globalAlpha = 0.55;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, Math.round(s.y) + 0.5);
    ctx.lineTo(vp.width, Math.round(s.y) + 0.5);
    ctx.moveTo(Math.round(s.x) + 0.5, 0);
    ctx.lineTo(Math.round(s.x) + 0.5, vp.height);
    ctx.stroke();
    ctx.globalAlpha = 1;

    // 픽박스 (선택 조리개)
    ctx.strokeRect(s.x - 5, s.y - 5, 10, 10);
  }

  // 5. 스냅 마커 — 종류를 눈으로 구분할 수 있어야 한다
  if (state.snap?.kind && state.cursor) {
    drawSnapMarker(ctx, vp.toScreen(state.snap.point), state.snap.kind, theme);
  }

  // 6. 추적선 (직교/극좌표)
  if (state.snap?.tracking) {
    const from = vp.toScreen(state.snap.tracking.from);
    const to = vp.toScreen(state.snap.point);
    ctx.strokeStyle = theme.snapMarker;
    ctx.globalAlpha = 0.5;
    ctx.setLineDash([3, 4]);
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
  }

  ctx.restore();
}

/** AutoCAD 와 같은 글리프를 쓴다 — 사용자가 모양만 보고 스냅 종류를 안다. */
function drawSnapMarker(
  ctx: CanvasRenderingContext2D,
  s: { x: number; y: number },
  kind: string,
  theme: RenderTheme
): void {
  const r = 6;
  ctx.strokeStyle = theme.snapMarker;
  ctx.lineWidth = 1.6;
  ctx.beginPath();

  switch (kind) {
    case 'endpoint': // □
      ctx.rect(s.x - r, s.y - r, r * 2, r * 2);
      break;
    case 'midpoint': // △
      ctx.moveTo(s.x, s.y - r);
      ctx.lineTo(s.x + r, s.y + r);
      ctx.lineTo(s.x - r, s.y + r);
      ctx.closePath();
      break;
    case 'center': // ○
      ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
      break;
    case 'quadrant': // ◇
      ctx.moveTo(s.x, s.y - r);
      ctx.lineTo(s.x + r, s.y);
      ctx.lineTo(s.x, s.y + r);
      ctx.lineTo(s.x - r, s.y);
      ctx.closePath();
      break;
    case 'intersection': // ×
      ctx.moveTo(s.x - r, s.y - r);
      ctx.lineTo(s.x + r, s.y + r);
      ctx.moveTo(s.x + r, s.y - r);
      ctx.lineTo(s.x - r, s.y + r);
      break;
    case 'perpendicular': // ⊥
      ctx.moveTo(s.x - r, s.y + r);
      ctx.lineTo(s.x + r, s.y + r);
      ctx.moveTo(s.x, s.y - r);
      ctx.lineTo(s.x, s.y + r);
      break;
    case 'insertion': // ⊡
      ctx.rect(s.x - r, s.y - r, r * 2, r * 2);
      ctx.moveTo(s.x - r / 2, s.y);
      ctx.lineTo(s.x + r / 2, s.y);
      break;
    default: // 근처점 — 모래시계
      ctx.moveTo(s.x - r, s.y - r);
      ctx.lineTo(s.x + r, s.y - r);
      ctx.lineTo(s.x - r, s.y + r);
      ctx.lineTo(s.x + r, s.y + r);
      ctx.closePath();
  }
  ctx.stroke();

  // 종류 이름을 작게 띄운다 — 처음 쓰는 사람에게 특히 도움이 된다
  const label = SNAP_LABEL[kind as keyof typeof SNAP_LABEL];
  if (label) {
    ctx.font = "11px 'Noto Sans KR', sans-serif";
    ctx.fillStyle = theme.snapMarker;
    ctx.textAlign = 'left';
    ctx.fillText(label, s.x + r + 4, s.y - r - 2);
  }
}

/* -------------------------------------------------------------------------- *
 * 치수 (P2-13)
 * -------------------------------------------------------------------------- */

/**
 * 치수선 · 치수보조선 · 화살표 · 실측 텍스트를 그린다.
 *
 * 마트 도면에서 치수는 "통로 폭 2400" 같은 검증 근거로 쓰이므로
 * 값이 도면 위에서 바로 읽혀야 한다. 배율이 작아 글자가 뭉개질 때는
 * 선만 그리고 텍스트는 생략한다 (읽을 수 없는 글자는 노이즈일 뿐이다).
 */
function drawDimension(
  ctx: CanvasRenderingContext2D,
  e: DimensionEntity,
  vp: Viewport,
  unit: DisplayUnit
): void {
  // 치수선이 놓일 위치를 결정한다.
  //   linear  — 가로/세로 중 긴 축에 평행하게
  //   aligned — 두 점을 잇는 방향에 평행하게
  let p1 = e.a;
  let p2 = e.b;

  if (e.kind === 'linear') {
    const dx = Math.abs(e.b.x - e.a.x);
    const dy = Math.abs(e.b.y - e.a.y);
    if (dx >= dy) {
      // 수평 치수 — 치수선의 y 는 offsetPoint 를 따른다
      p1 = { x: e.a.x, y: e.offsetPoint.y };
      p2 = { x: e.b.x, y: e.offsetPoint.y };
    } else {
      p1 = { x: e.offsetPoint.x, y: e.a.y };
      p2 = { x: e.offsetPoint.x, y: e.b.y };
    }
  } else {
    // aligned / radius — offsetPoint 만큼 평행 이동
    const ox = e.offsetPoint.x - (e.a.x + e.b.x) / 2;
    const oy = e.offsetPoint.y - (e.a.y + e.b.y) / 2;
    p1 = { x: e.a.x + ox, y: e.a.y + oy };
    p2 = { x: e.b.x + ox, y: e.b.y + oy };
  }

  const s1 = vp.toScreen(p1);
  const s2 = vp.toScreen(p2);
  const e1 = vp.toScreen(e.a);
  const e2 = vp.toScreen(e.b);

  // 치수보조선 (측정점 → 치수선)
  ctx.save();
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(e1.x, e1.y);
  ctx.lineTo(s1.x, s1.y);
  ctx.moveTo(e2.x, e2.y);
  ctx.lineTo(s2.x, s2.y);
  ctx.stroke();

  // 치수선
  ctx.beginPath();
  ctx.moveTo(s1.x, s1.y);
  ctx.lineTo(s2.x, s2.y);
  ctx.stroke();

  // 양 끝 화살표
  const ang = Math.atan2(s2.y - s1.y, s2.x - s1.x);
  arrow(ctx, s1, ang);
  arrow(ctx, s2, ang + Math.PI);

  // 실측 텍스트
  //
  // ★ 텍스트 크기는 실세계 200mm 기준을 화면 배율로 환산한 값이다 — 매장
  //   전체(수십 m)를 한 화면에 맞춰 축소해서 보면 이 값이 1px 안팎으로
  //   줄어들어 사실상 안 보이게 된다. 치수선·화살표는 보이는데 숫자만
  //   사라지는 것처럼 느껴져 "치수가 표시 안 된다" 로 오인하기 쉽다.
  //   그래서 화면 배율과 무관하게 최소 읽을 수 있는 크기(11px)는 보장한다
  //   (Annotative 텍스트처럼 — 실측 크기가 그보다 크면 그 값을 그대로 쓴다).
  const h = Math.max(11, vp.worldToPx(e.textHeight));
  const label = dimensionLabel(e, unit);
  const mx = (s1.x + s2.x) / 2;
  const my = (s1.y + s2.y) / 2;

  ctx.save();
  ctx.translate(mx, my);
  // 글자가 거꾸로 서지 않도록 회전을 보정한다
  let t = ang;
  if (t > Math.PI / 2 || t < -Math.PI / 2) t += Math.PI;
  ctx.rotate(t);
  ctx.font = `${h}px 'Noto Sans KR', sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillText(label, 0, -3);
  ctx.restore();

  ctx.restore();
}

function arrow(
  ctx: CanvasRenderingContext2D,
  at: { x: number; y: number },
  angle: number
): void {
  const len = 7;
  const spread = 0.42;
  ctx.beginPath();
  ctx.moveTo(at.x, at.y);
  ctx.lineTo(at.x + Math.cos(angle - spread) * len, at.y + Math.sin(angle - spread) * len);
  ctx.moveTo(at.x, at.y);
  ctx.lineTo(at.x + Math.cos(angle + spread) * len, at.y + Math.sin(angle + spread) * len);
  ctx.stroke();
}
